/**
 * Normalize External API Error
 * 
 * Provides consistent error normalization for Google Calendar and Microsoft Graph APIs.
 * Extracts rate limit info, retry timing, and categorizes errors.
 * 
 * @module calendar/integrations/normalizeExternalApiError
 */

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalized error from external calendar API.
 */
export interface NormalizedApiError {
  /** Error code for programmatic handling */
  code: 'rate_limit' | 'auth' | 'not_found' | 'forbidden' | 'server_error' | 'network' | 'unknown';
  
  /** Human-readable error message */
  message: string;
  
  /** Retry delay in milliseconds (from Retry-After header or calculated) */
  retryAfterMs?: number;
  
  /** Whether this is a rate limit error (429 or Google 403 quota) */
  isRateLimit: boolean;
  
  /** Whether this is an authentication/authorization error */
  isAuth: boolean;
  
  /** Original HTTP status code if available */
  httpStatus?: number;
  
  /** Provider that generated the error */
  provider: 'google' | 'microsoft';
  
  /** Raw error details for logging */
  rawError?: string;
}

/**
 * Options for error normalization.
 */
export interface NormalizeErrorOptions {
  /** Provider context */
  provider: 'google' | 'microsoft';
  
  /** Optional HTTP response for header extraction */
  response?: Response | { status?: number; headers?: Headers | Record<string, string> };
  
  /** Default retry delay if not specified by provider */
  defaultRetryMs?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

/** Default retry delay when Retry-After not provided (60 seconds) */
const DEFAULT_RATE_LIMIT_RETRY_MS = 60_000;

/** Maximum retry delay cap (5 minutes) */
const MAX_RETRY_DELAY_MS = 300_000;

/** Minimum retry delay (1 second) */
const MIN_RETRY_DELAY_MS = 1_000;

// ─────────────────────────────────────────────────────────────────────────────
// Google-specific patterns
// ─────────────────────────────────────────────────────────────────────────────

/** Google API error patterns indicating rate limits */
const GOOGLE_RATE_LIMIT_PATTERNS = [
  'rate limit exceeded',
  'quota exceeded',
  'user rate limit',
  'daily limit exceeded',
  'too many requests',
  'userlimitexceeded',
  'ratelimitexceeded',
  'quotaexceeded',
];

/** Google API error patterns indicating auth issues */
const GOOGLE_AUTH_PATTERNS = [
  'invalid_grant',
  'token expired',
  'token has been expired',
  'unauthorized',
  'invalid credentials',
  'access_denied',
  'consent_required',
];

// ─────────────────────────────────────────────────────────────────────────────
// Microsoft-specific patterns
// ─────────────────────────────────────────────────────────────────────────────

/** Microsoft Graph error patterns indicating rate limits */
const MICROSOFT_RATE_LIMIT_PATTERNS = [
  'activitylimitreached',
  'applicationthrottled',
  'too many requests',
];

/** Microsoft Graph error patterns indicating auth issues */
const MICROSOFT_AUTH_PATTERNS = [
  'invalidauthenticationtoken',
  'authorization_pending',
  'interaction_required',
  'consent_required',
  'token expired',
  'unauthorized',
];

// ─────────────────────────────────────────────────────────────────────────────
// Main Function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Normalizes errors from external calendar APIs into a consistent format.
 * 
 * Handles:
 * - Google Calendar API: 429, 403 (quota), auth errors
 * - Microsoft Graph API: 429 with Retry-After header
 * 
 * @param error - The error from the API call (Error object, string, or response)
 * @param options - Normalization options including provider
 * @returns Normalized error object
 * 
 * @example
 * ```typescript
 * try {
 *   await googleApi.events.insert(...);
 * } catch (error) {
 *   const normalized = normalizeExternalApiError(error, { provider: 'google' });
 *   if (normalized.isRateLimit && normalized.retryAfterMs) {
 *     await delay(normalized.retryAfterMs);
 *   }
 * }
 * ```
 */
export function normalizeExternalApiError(
  error: unknown,
  options: NormalizeErrorOptions
): NormalizedApiError {
  const { provider, response, defaultRetryMs = DEFAULT_RATE_LIMIT_RETRY_MS } = options;
  
  // Extract error message
  const errorMessage = extractErrorMessage(error);
  const lowerMessage = errorMessage.toLowerCase();
  
  // Extract HTTP status
  const httpStatus = extractHttpStatus(error, response);
  
  // Extract Retry-After header
  const retryAfterFromHeader = extractRetryAfter(response);
  
  // Detect rate limit
  const isRateLimit = detectRateLimit(httpStatus, lowerMessage, provider);
  
  // Detect auth error
  const isAuth = detectAuthError(httpStatus, lowerMessage, provider);
  
  // Calculate retry delay
  const retryAfterMs = isRateLimit 
    ? calculateRetryDelay(retryAfterFromHeader, defaultRetryMs, provider)
    : undefined;
  
  // Determine error code
  const code = categorizeError(httpStatus, isRateLimit, isAuth, lowerMessage);
  
  // Build normalized error
  return {
    code,
    message: buildUserMessage(code, errorMessage, provider, retryAfterMs),
    retryAfterMs,
    isRateLimit,
    isAuth,
    httpStatus,
    provider,
    rawError: errorMessage,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Extracts error message from various error types.
 */
function extractErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error && typeof error === 'object') {
    // Handle Google API error format
    const googleError = error as { error?: { message?: string; code?: number } };
    if (googleError.error?.message) {
      return googleError.error.message;
    }
    
    // Handle Microsoft Graph error format
    const msError = error as { error?: { message?: string; code?: string } };
    if (msError.error?.message) {
      return msError.error.message;
    }
    
    // Generic message extraction
    const anyError = error as { message?: string };
    if (anyError.message) {
      return anyError.message;
    }
  }
  return String(error);
}

/**
 * Extracts HTTP status code from error or response.
 */
function extractHttpStatus(
  error: unknown, 
  response?: Response | { status?: number; headers?: Headers | Record<string, string> }
): number | undefined {
  // From response object
  if (response?.status) {
    return response.status;
  }
  
  // From error object
  if (error && typeof error === 'object') {
    const anyError = error as { status?: number; code?: number; response?: { status?: number } };
    if (anyError.status) return anyError.status;
    if (anyError.code && typeof anyError.code === 'number') return anyError.code;
    if (anyError.response?.status) return anyError.response.status;
  }
  
  // Try to parse from error message
  const errorStr = String(error);
  const statusMatch = errorStr.match(/\b(4\d{2}|5\d{2})\b/);
  if (statusMatch) {
    return parseInt(statusMatch[1], 10);
  }
  
  return undefined;
}

/**
 * Extracts Retry-After header value in milliseconds.
 */
function extractRetryAfter(
  response?: Response | { status?: number; headers?: Headers | Record<string, string> }
): number | undefined {
  if (!response?.headers) return undefined;
  
  let retryAfterValue: string | null = null;
  
  if (response.headers instanceof Headers) {
    retryAfterValue = response.headers.get('Retry-After');
  } else if (typeof response.headers === 'object') {
    retryAfterValue = (response.headers as Record<string, string>)['Retry-After'] 
      || (response.headers as Record<string, string>)['retry-after'];
  }
  
  if (!retryAfterValue) return undefined;
  
  // Retry-After can be seconds or HTTP date
  const seconds = parseInt(retryAfterValue, 10);
  if (!isNaN(seconds)) {
    return seconds * 1000; // Convert to milliseconds
  }
  
  // Try parsing as HTTP date
  const date = new Date(retryAfterValue);
  if (!isNaN(date.getTime())) {
    return Math.max(0, date.getTime() - Date.now());
  }
  
  return undefined;
}

/**
 * Detects if error is a rate limit error.
 */
function detectRateLimit(
  httpStatus: number | undefined,
  lowerMessage: string,
  provider: 'google' | 'microsoft'
): boolean {
  // HTTP 429 is always rate limit
  if (httpStatus === 429) return true;
  
  // Google uses 403 for some quota errors
  if (provider === 'google' && httpStatus === 403) {
    if (GOOGLE_RATE_LIMIT_PATTERNS.some(p => lowerMessage.includes(p))) {
      return true;
    }
  }
  
  // Check message patterns
  const patterns = provider === 'google' 
    ? GOOGLE_RATE_LIMIT_PATTERNS 
    : MICROSOFT_RATE_LIMIT_PATTERNS;
    
  return patterns.some(pattern => lowerMessage.includes(pattern));
}

/**
 * Detects if error is an authentication/authorization error.
 */
function detectAuthError(
  httpStatus: number | undefined,
  lowerMessage: string,
  provider: 'google' | 'microsoft'
): boolean {
  // HTTP 401 is always auth
  if (httpStatus === 401) return true;
  
  // Check message patterns
  const patterns = provider === 'google' 
    ? GOOGLE_AUTH_PATTERNS 
    : MICROSOFT_AUTH_PATTERNS;
    
  return patterns.some(pattern => lowerMessage.includes(pattern));
}

/**
 * Calculates retry delay with jitter for exponential backoff.
 */
function calculateRetryDelay(
  retryAfterFromHeader: number | undefined,
  defaultRetryMs: number,
  _provider: 'google' | 'microsoft'
): number {
  // Prefer Retry-After header (especially for Microsoft Graph)
  if (retryAfterFromHeader !== undefined) {
    // Add small jitter (0-10% of delay)
    const jitter = Math.random() * 0.1 * retryAfterFromHeader;
    return clampDelay(retryAfterFromHeader + jitter);
  }
  
  // Use default with jitter
  // Google recommends random jitter for quota errors
  const jitterMultiplier = 0.5 + Math.random(); // 0.5x to 1.5x
  const delay = defaultRetryMs * jitterMultiplier;
  
  return clampDelay(delay);
}

/**
 * Clamps delay to min/max bounds.
 */
function clampDelay(delay: number): number {
  return Math.min(MAX_RETRY_DELAY_MS, Math.max(MIN_RETRY_DELAY_MS, Math.round(delay)));
}

/**
 * Categorizes error into a code.
 */
function categorizeError(
  httpStatus: number | undefined,
  isRateLimit: boolean,
  isAuth: boolean,
  lowerMessage: string
): NormalizedApiError['code'] {
  if (isRateLimit) return 'rate_limit';
  if (isAuth) return 'auth';
  
  if (httpStatus === 404) return 'not_found';
  if (httpStatus === 403) return 'forbidden';
  if (httpStatus && httpStatus >= 500) return 'server_error';
  
  if (lowerMessage.includes('network') || lowerMessage.includes('fetch')) {
    return 'network';
  }
  
  return 'unknown';
}

/**
 * Builds a user-friendly error message.
 */
function buildUserMessage(
  code: NormalizedApiError['code'],
  rawMessage: string,
  provider: 'google' | 'microsoft',
  retryAfterMs?: number
): string {
  const providerName = provider === 'google' ? 'Google Calendar' : 'Microsoft';
  
  switch (code) {
    case 'rate_limit':
      const waitTime = retryAfterMs 
        ? `${Math.ceil(retryAfterMs / 1000)} seconds` 
        : 'a moment';
      return `${providerName} rate limit reached. Please wait ${waitTime} before retrying.`;
      
    case 'auth':
      return `${providerName} authentication failed. Please reconnect your account.`;
      
    case 'not_found':
      return `${providerName} event not found. It may have been deleted.`;
      
    case 'forbidden':
      return `${providerName} access denied. Check your calendar permissions.`;
      
    case 'server_error':
      return `${providerName} is temporarily unavailable. Please try again later.`;
      
    case 'network':
      return `Unable to reach ${providerName}. Check your internet connection.`;
      
    default:
      return rawMessage || `${providerName} sync failed.`;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Utility Exports
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Adds jitter to an exponential backoff delay.
 * 
 * @param baseDelay - Base delay in milliseconds
 * @param attempt - Current attempt number (0-indexed)
 * @param jitterFactor - Jitter factor (default 0.3 = ±30%)
 * @returns Delay with jitter applied
 */
export function addJitterToBackoff(
  baseDelay: number,
  attempt: number,
  jitterFactor = 0.3
): number {
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = exponentialDelay * jitterFactor * (Math.random() * 2 - 1); // ±jitterFactor
  return clampDelay(exponentialDelay + jitter);
}

/**
 * Calculates provider-aware retry delay.
 * 
 * @param provider - Calendar provider
 * @param attempt - Current attempt number
 * @param normalizedError - Optional normalized error with retryAfterMs
 * @returns Delay in milliseconds
 */
export function getProviderAwareRetryDelay(
  provider: 'google' | 'microsoft',
  attempt: number,
  normalizedError?: NormalizedApiError
): number {
  // Prefer Retry-After from error if available
  if (normalizedError?.retryAfterMs) {
    return normalizedError.retryAfterMs;
  }
  
  // Provider-specific base delays
  const baseDelay = provider === 'google' ? 1000 : 2000;
  
  return addJitterToBackoff(baseDelay, attempt);
}
